import actionHelpers from '../../utils/action-helpers';
import { buildUrl, sort } from '../../utils/helpers';
import axios from 'axios';
const path = process.env.VUE_APP_ROOT_API;

const state = {
  status: null,
  items: [],
  heading: 'Groups',
  description: '',
  nextUrl: null,
  total: null,
  errors: [],
  filters: {
    brandId: [],
    generationId: [],
    originId: [],
    yearId: [],
    releaseId: [],
    manufacturerId: [],
    countryId: []
  },
  displayStyle: 'grid',
  sort: {
    direction: 'asc',
    field: 'name'
  }
};

// getters
const getters = {
  getAll: state => state.items,
  getById: state => id => {
    const grouping = state.items.find(o => {
      return o.id === parseInt(id, 10);
    });

    return (grouping && Object.keys(grouping).length) ? grouping : {};
  },
  getBySlug: state => slug => {
    const grouping = state.items.find(o => {
      return o.slug === slug;
    });

    return (grouping && Object.keys(grouping).length) ? grouping : {};
  },
  getGroupsByBrand: state => slug => {
    const groupings = state.items.filter(o => {
      return o.brand.slug === slug;
    });

    return (groupings && groupings.length) ? groupings : [];
  },
  getGroupsByRelease: state => slug => {
    const groupings = state.items.filter(o => {
      return o.release.slug === slug;
    });

    return (groupings && groupings.length) ? groupings : [];
  },
  getGroupsByOrigin: state => slug => {
    const groupings = state.items.filter(o => {
      return o.origin.slug === slug;
    });

    return (groupings && groupings.length) ? groupings : [];
  },
  getGroupsByYear: state => slug => {
    const groupings = state.items.filter(o => {
      return o.year.slug === slug;
    });

    return (groupings && groupings.length) ? groupings : [];
  },
  getGroupsByManufacturer: state => slug => {
    const groupings = state.items.filter(o => {
      return o.manufacturer.slug === slug;
    });

    return (groupings && groupings.length) ? groupings : [];
  },
  getGroupsByGeneration: state => slug => {
    const groupings = state.items.filter(o => {
      return o.generation.slug === slug;
    });

    return (groupings && groupings.length) ? groupings : [];
  },
  getGroupsByCountry: state => slug => {
    const groupings = state.items.filter(o => {
      return o.countries.find(country => {
        return country.slug === slug;
      });
    });

    return (groupings && groupings.length) ? groupings : [];
  },
  getNextUrl: state => slug => {
    const grouping = state.items.find(o => {
      return o.slug === slug;
    });

    return grouping ? grouping.ponies?.nextUrl : null;
  },
  getGroupPonies: state => slug => {
    const grouping = state.items.find(o => {
      return o.slug === slug;
    });

    return (grouping.ponies?.items && grouping.ponies.items.length) ? grouping.ponies.items : [];
  },
  getRelatedGroups: state => slug => {
    const grouping = state.items.find(o => {
      return o.slug === slug;
    });

    return (grouping.related?.items && grouping.related.items.length) ? grouping.related.items : [];
  },
  getFilters: state => () => {
    return state.filters;
  },
};

// actions
const actions = {
  RESET_ERRORS: actionHelpers.RESET_ERRORS,
  RESET_STATUS: actionHelpers.RESET_STATUS,
  CLEAR_ITEMS: ({ commit }) => {
    commit('CLEAR_ITEMS');
  },
  FETCH_ALL: ({ commit, dispatch }, payload) => {
    // fetch an initial list of brands
    commit('CLEAR_ITEMS');

    const sort = () =>
      dispatch('SORT', {
        direction: state.sort.direction,
        field: state.sort.field
      });

    actionHelpers.FETCH_ALL(`${path}/api/groupings/${payload.num}`, sort)({
      commit,
      dispatch
    });
  },
  FETCH_MORE: ({ commit, dispatch }, payload) => {
    // fetch the next page of groupings if there is a next page
    let url = buildUrl(`${state.nextUrl}`, payload);

    actionHelpers.FETCH_MORE(url)({ commit, dispatch });
  },
  FETCH: async ({ commit, dispatch }, { slug, num }) => {
    // check to see if grouping is already in list
    // TODO: add num groupings to api so we can avoid refetching groupings
    const inList = state.items.find(o => {
      return o.slug === slug;
    });

    if (!inList) {
      try {
        const { data } = await axios.get(`${path}/api/grouping/${slug}`);
        commit('ADD_ITEM', data.item);
        dispatch('FETCH_PONIES', { slug, num });
        dispatch('FETCH_RELATED_GROUPINGS', { slug, num });
      } catch (error) {
        console.error(error);
        commit('SET_ERROR', error);
        dispatch(
          'notification/ERROR_NOTIFICATION',
          'could not fetch grouping. try again later.',
          { root: true }
        );
      }
    } else {
      dispatch('FETCH_PONIES', { slug, num });
    }
  },
  UPDATE_FILTERS: ({ commit, dispatch }, payload) => {
    commit('UPDATE_FILTERS', payload);
    dispatch('FILTER');
  },
  FILTER: async ({ commit, dispatch }) => {
    commit('UPDATE_STATUS', 'loading');
    let url = buildUrl(`${path}/api/groupings/`, state.filters);

    try {
      const { data } = await axios.get(url);

      commit('UPDATE_STATUS', 'success');
      commit('SET_ITEMS', data);
    } catch (error) {
      commit('UPDATE_STATUS', 'error');
      dispatch(
        'notification/ERROR_NOTIFICATION',
        'could not fetch items. try again later.',
        { root: true }
      );
    }
  },
  FETCH_RELATED_GROUPINGS: async ({ commit, dispatch }, { slug, num }) => {
    try {
      const {
        data: {
          data: { items },
          links,
          meta
        }
      } = await axios.get(
        `${path}/api/grouping/${slug}/related/${num}`
      );
      commit('SET_RELATED_ITEMS', {
        slug,
        groupings: items,
        links: links,
        meta: meta
      });
    } catch (error) {
      commit('SET_ERROR', error);
      dispatch(
        'notification/ERROR_NOTIFICATION',
        'could not fetch related groups. try again later.',
        { root: true }
      );
    }
  },
  FETCH_PONIES: ({ commit, dispatch }, { slug, num }) => {
    actionHelpers.FETCH_PONIES(
      `${path}/api/grouping/${slug}/ponies/${num}`
    )({ commit, dispatch }, { slug, num });
  },
  FETCH_MORE_PONIES: async ({ commit, dispatch }, payload) => {
    actionHelpers.FETCH_MORE_PONIES(payload.url)({ commit, dispatch }, payload);
  },
  ADD: async ({ commit, dispatch }, payload) => {
    actionHelpers.ADD(`${path}/api/grouping/create`)({ commit, dispatch }, payload);
  },
  UPDATE: async ({ commit, dispatch }, payload) => {
    actionHelpers.UPDATE(`${path}/api/grouping/${payload.slug}`)(
      { commit, dispatch },
      payload
    );
  },
  REMOVE: async ({ commit, dispatch }, payload) => {
    actionHelpers.REMOVE(`${path}/api/grouping/${payload.slug}`)(
      { commit, dispatch },
      payload
    );
  },
  SET_ERROR: ({ commit }, { errors }) => {
    if (errors && errors.response) {
      commit('SET_ERROR', errors.response.data.errors);
    } else if (errors) {
      commit('SET_ERROR', errors);
    }
  },
  CHANGE_DISPLAY_STYLE: actionHelpers.CHANGE_DISPLAY_STYLE,
  SORT: ({ commit }, { direction, field }) => {
    commit('SORT_GROUPINGS', { direction, field });
  }
};

// mutations
const mutations = {
  UPDATE_STATUS: (state, status) => (state.status = status),
  RESET_ERRORS: state => (state.errors = []),
  SET_ERROR: (state, errors) => {
    state.errors = state.errors.concat(errors);
  },
  SET_ITEMS: (state, data) => {
    state.items = data.data.items;
    state.heading = data.data.heading;
    state.description = data.data.description;
    state.nextUrl = data.links.next;
    state.total = data.meta.total;
  },
  ADD_ITEMS: (state, { data: { items }, links: { next }, meta: { total } }) => {
    state.items = state.items.concat(items);
    state.nextUrl = next;
    state.total = total;
  },
  SET_RELATED_ITEMS: (state, { slug, groupings, links : { next } }) => {
    state.items = state.items.map(o => {
      if (o.slug === slug) {
        o.related = {
          items: groupings,
          nextUrl: next
        };

        return o;
      }
      return o;
    });
  },
  ADD_ITEM: (state, data) => {
    state.items = state.items.concat(data);
  },
  UPDATE_ITEM: (state, { slug, name, description }) => {
    // fetch next "page" of groups that belong to the grouping
    state.items = state.items.map(o => {
      o.slug === slug;
      o.name = name;
      o.description = description;

      return o;
    });
  },
  REMOVE_ITEM: (state, slug) => {
    state.items = state.items.filter(o => {
      o.slug !== slug;
    });
  },
  SET_NEXT_URL: (state, { slug, url }) => {
    state.items = state.items.map(o => {
      if (o.slug === slug) {
        o.nextUrl = url;

        return o;
      }

      return o;
    });
  },
  UPDATE_FILTERS: (state, { name, value }) => {
    const filters = { ...state.filters };
    // toggle filter values
    if (filters[name] && filters[name].find(o => o === value)) {
      filters[name] = filters[name].filter(o => o !== value);
    } else if (filters[name]) {
      filters[name].push(value);
      // } else {
      //   console.log(`there is no ${name}`);
    }

    state.filters = filters;
  },
  UPDATE_DISPLAY_STYLE(state, { display }) {
    state.displayStyle = display;
  },
  SORT_GROUPINGS(state, { direction, field = 'name' }) {
    state.sort = { direction, field };

    if (state.items && state.items.length) {
      state.items = sort([...state.items], direction, field);
    }
  },
  CLEAR_ITEMS(state) {
    state.items = [];
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
